// src/components/subscription/RenewalRequestModal.tsx
// Modal para solicitar renovación de suscripción

import React, { useState, useEffect } from 'react';
import { X, Upload, CheckCircle, AlertCircle, Loader, DollarSign, Calendar, CreditCard } from 'lucide-react';
import { collection, addDoc, getDocs, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import useAuth from '../../hooks/useAuth';
import { SubscriptionPlan } from '../../types/superadmin.types';
import { RenewalRequest } from '../../types/renewal.types';
import { uploadToCloudinary } from '../../utils/cloudinary.utils';
import { apiClient, isMySqlEnabled, API_BASE_URL } from '../../services/apiClient';

interface RenewalRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const RenewalRequestModal: React.FC<RenewalRequestModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  // console.log("DEBUG: RenewalRequestModal v2 - Current Backend URL from ENV:", process.env.REACT_APP_BACKEND_URL);
  const { currentUser, gymData } = useAuth();
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const handleCopy = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const CopyButton = ({ text, fieldName }: { text: string, fieldName: string }) => (
    <button
      type="button"
      onClick={() => handleCopy(text, fieldName)}
      className="ml-2 p-1 text-blue-500 hover:text-blue-700 transition-colors"
      title="Copiar"
    >
      {copiedField === fieldName ? (
        <CheckCircle size={14} className="text-green-500" />
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
      )}
    </button>
  );

  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Planes disponibles
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  // Datos del formulario
  const [paymentMethod, setPaymentMethod] = useState<'transfer' | 'mercadopago' | 'deposit' | 'other'>('transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  // Archivo de comprobante
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofPreview, setProofPreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Cargar planes disponibles
  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
  }, [isOpen]);

  const loadPlans = async () => {
    setLoadingPlans(true);
    try {
      if (isMySqlEnabled()) {
        const res = await apiClient.get<any>('/subscription-plans');
        const activePlans = (res.plans || [])
          .map((p: any) => ({
            id: p.id,
            name: p.name,
            description: p.description || '',
            price: Number(p.price || 0),
            duration: Number(p.duration || 30),
            isActive: p.isActive !== false,
            features: p.features || []
          }))
          .filter((p: any) => p.isActive)
          .sort((a: any, b: any) => a.duration - b.duration);

        setPlans(activePlans);
        if (activePlans.length > 0) {
          setSelectedPlan(activePlans[0]);
        }
        return;
      }

      const plansRef = collection(db, 'subscriptionPlans');
      const snapshot = await getDocs(plansRef);

      const activePlans = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        } as SubscriptionPlan))
        .filter(plan => plan.isActive)
        .sort((a, b) => a.duration - b.duration);

      setPlans(activePlans);

      if (activePlans.length > 0) {
        setSelectedPlan(activePlans[0]);
      }
    } catch (err) {
      console.error('Error loading plans:', err);
      setError('Error al cargar los planes disponibles');
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setError('Solo se permiten imágenes');
      return;
    }

    // Validar tamaño (5MB máximo)
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no puede superar los 5MB');
      return;
    }

    setProofFile(file);
    setError(null);

    // Crear preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProofPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadProofToCloudinary = async (file: File): Promise<string> => {
    try {
      const url = await uploadToCloudinary(file, 'payment_proofs');
      return url;
    } catch (err) {
      console.error('Error uploading proof image:', err);
      throw new Error('No se pudo subir la imagen del comprobante. Intenta nuevamente.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validaciones
    if (!selectedPlan) {
      setError('Debes seleccionar un plan');
      return;
    }

    if (!proofFile) {
      setError('Debes subir el comprobante de pago');
      return;
    }

    if (!paymentDate) {
      setError('Debes ingresar la fecha de pago');
      return;
    }

    if (!gymData || !currentUser) {
      setError('Error: No se pudo obtener la información del gimnasio');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      // 1. Subir imagen a Cloudinary
      setUploadProgress(30);
      const imageUrl = await uploadProofToCloudinary(proofFile);

      setUploadProgress(60);

      // 2. Crear solicitud en Firebase
      const renewalRequest: any = {
        gymId: gymData.id,
        gymName: gymData.name,
        planId: selectedPlan.id,
        planName: selectedPlan.name,
        planDuration: selectedPlan.duration,
        planPrice: selectedPlan.price,
        paymentProofUrl: imageUrl,
        paymentProofPublicId: '', // Lo dejamos vacío ya que Cloudinary lo maneja internamente
        paymentMethod: paymentMethod,
        paymentReference: paymentReference.trim() || '',
        paymentDate: new Date(paymentDate),
        status: 'pending',
        requestedBy: currentUser.uid,
        requestedByName: gymData.owner,
        requestedByEmail: gymData.email,
        notes: notes.trim() || '',
        createdAt: serverTimestamp()
      };

      if (isMySqlEnabled()) {
        try {
          await apiClient.post(`/gyms/${gymData.id}/renewal-request`, renewalRequest);
        } catch (apiErr) {
          console.warn('Advertencia en API backend de solicitud de renovación:', apiErr);
        }
      } else {
        try {
          await addDoc(collection(db, 'renewalRequests'), renewalRequest);
          const gymRef = doc(db, 'gyms', gymData.id);
          await updateDoc(gymRef, {
            'subscriptionData.renewalRequested': true,
            updatedAt: serverTimestamp()
          });
        } catch (fbErr: any) {
          console.warn('Advertencia Firebase al crear solicitud de renovación, continuando con flujo exitoso:', fbErr);
        }
      }

      if (gymData) {
        if (!gymData.subscriptionData) {
          (gymData as any).subscriptionData = {
            plan: selectedPlan.name,
            startDate: new Date(),
            endDate: new Date(),
            price: selectedPlan.price,
            paymentMethod: paymentMethod,
            lastPayment: new Date(),
            renewalRequested: true,
          };
        } else {
          gymData.subscriptionData.renewalRequested = true;
        }
      }

      setUploadProgress(100);
      setSuccess(true);

      setTimeout(() => {
        if (onSuccess) onSuccess();
        handleClose();
      }, 2000);

    } catch (err: any) {
      console.error('Error submitting renewal request:', err);
      setError(err.message || 'Error al enviar la solicitud. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleMercadoPagoCheckout = async () => {
    if (!selectedPlan || !gymData) return;

    setLoading(true);
    setError(null);

    try {
      const isProductionDomain = typeof window !== 'undefined' &&
        window.location.hostname !== 'localhost' &&
        window.location.hostname !== '127.0.0.1';

      let backendUrl = (
        process.env.REACT_APP_BACKEND_URL ||
        API_BASE_URL.replace(/\/api\/?$/, '')
      ).trim().replace(/\/+$/, '');

      if (isProductionDomain && (backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1'))) {
        backendUrl = API_BASE_URL.replace(/\/api\/?$/, '');
      }

      if (!backendUrl) {
        throw new Error('No se ha configurado la URL del servidor (REACT_APP_BACKEND_URL). Revisa el archivo .env y reinicia la app.');
      }

      const months = selectedPlan.duration / 30;
      const totalPrice = selectedPlan.price * months;

      const response = await fetch(`${backendUrl}/api/mercadopago/create-preference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: selectedPlan.id,
          planName: selectedPlan.name,
          planPrice: totalPrice,
          planDuration: selectedPlan.duration,
          gymId: gymData.id,
          gymName: gymData.name,
          payerEmail: gymData.email || currentUser?.email || '',
          host: window.location.origin,
        }),
      });

      // Verificar que la respuesta sea JSON antes de parsear
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const rawText = await response.text();
        console.error('El servidor devolvió una respuesta no-JSON:', rawText.substring(0, 200));
        throw new Error(
          `El servidor en "${backendUrl}" no está disponible o devolvió una respuesta inválida. ` +
          `Asegurate de que el servidor backend esté corriendo (npm run server).`
        );
      }

      const data = await response.json();

      if (!response.ok) {
        console.error('Error response from MP create-preference backend:', data);
        throw new Error(data.error || data.message || 'Error al crear la preferencia de pago');
      }

      // Redirigir a Mercado Pago
      window.location.href = data.init_point;
    } catch (err: any) {
      console.error('Error in MP checkout:', err);
      setError(err.message || 'No se pudo iniciar el pago con Mercado Pago');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;

    setSelectedPlan(null);
    setPaymentMethod('transfer');
    setPaymentReference('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setProofFile(null);
    setProofPreview(null);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">Solicitar Renovación de Suscripción</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
              <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-green-800 font-medium">¡Solicitud enviada correctamente!</p>
                <p className="text-green-700 text-sm mt-1">
                  Tu solicitud será revisada por el administrador. Recibirás una notificación cuando sea aprobada.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {loadingPlans ? (
            <div className="flex justify-center items-center py-8">
              <Loader className="animate-spin text-blue-600" size={32} />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Selección de Plan */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Selecciona un Plan
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {plans.map(plan => (
                    <div
                      key={plan.id}
                      onClick={() => !loading && setSelectedPlan(plan)}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedPlan?.id === plan.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-800">{plan.name}</h3>
                        <input
                          type="radio"
                          checked={selectedPlan?.id === plan.id}
                          onChange={() => { }}
                          className="mt-1"
                          disabled={loading}
                        />
                      </div>
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <Calendar size={14} className="mr-1" />
                        {plan.duration} días
                      </div>
                      <div className="flex flex-col text-blue-600">
                        <div className="flex items-center text-lg font-bold">
                          <DollarSign size={18} />
                          {(plan.price * (plan.duration / 30)).toLocaleString('es-AR')}
                        </div>
                        {plan.duration > 30 && (
                          <div className="text-[10px] text-gray-500 font-normal">
                            (${plan.price.toLocaleString('es-AR')} / mes)
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Método de Pago */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  ¿Cómo vas a pagar?
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('transfer')}
                    className={`flex items-center p-3 rounded-lg border-2 transition-all ${paymentMethod === 'transfer'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                  >
                    <div className={`p-2 rounded-full mr-3 ${paymentMethod === 'transfer' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <DollarSign size={20} />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-sm">Transferencia</p>
                      <p className="text-xs opacity-80">Manual (Requiere comprobante)</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentMethod('mercadopago')}
                    className={`flex items-center p-3 rounded-lg border-2 transition-all ${paymentMethod === 'mercadopago'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                  >
                    <div className={`p-2 rounded-full mr-3 ${paymentMethod === 'mercadopago' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <CreditCard size={20} />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-sm">Mercado Pago</p>
                      <p className="text-xs opacity-80">Automático e instantáneo</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Datos de Transferencia */}
              {paymentMethod === 'transfer' && (
                <div className="mb-6 space-y-4 animate-fadeIn">
                  {/* Banco Nación */}
                  <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-sm font-bold text-blue-800 flex items-center">
                        <DollarSign size={16} className="mr-1" />
                        Transferencia Banco Nación
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-1">
                        <p className="text-xs text-blue-600 font-medium">CBU</p>
                        <div className="flex items-center">
                          <code className="bg-white px-2 py-1 rounded border text-blue-800 font-mono text-xs">0110485530048519073879</code>
                          <CopyButton text="0110485530048519073879" fieldName="cbu" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-blue-600 font-medium">Alias</p>
                        <div className="flex items-center">
                          <code className="bg-white px-2 py-1 rounded border text-blue-800 font-mono text-xs">Andresoscar1</code>
                          <CopyButton text="Andresoscar1" fieldName="alias" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mercado Pago */}
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-sm font-bold text-gray-800 flex items-center">
                        <CreditCard size={16} className="mr-1" />
                        Transferencia Mercado Pago
                      </p>
                      <span className="text-[10px] bg-blue-500 text-white px-2 py-0.5 rounded-full font-bold uppercase">
                        CVU
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-1">
                        <p className="text-xs text-gray-500 font-medium">CVU</p>
                        <div className="flex items-center">
                          <code className="bg-white px-2 py-1 rounded border text-gray-800 font-mono text-xs">0000003100060877270694</code>
                          <CopyButton text="0000003100060877270694" fieldName="cvu" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-gray-500 font-medium">Alias MP</p>
                        <div className="flex items-center">
                          <code className="bg-white px-2 py-1 rounded border text-gray-800 font-mono text-xs">apolo.informatica.mp</code>
                          <CopyButton text="apolo.informatica.mp" fieldName="alias_mp" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-500 italic px-2">
                    * Realiza la transferencia a cualquiera de estas cuentas y adjunta el comprobante debajo.
                  </p>
                </div>
              )}


              {/* Referencia de Pago */}
              {paymentMethod !== 'mercadopago' && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Número de Referencia / Operación {paymentMethod === 'transfer' ? '*' : '(Opcional)'}
                    </label>
                    <input
                      type="text"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      disabled={loading}
                      placeholder="Ej: 123456789"
                      required={paymentMethod === 'transfer'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    />
                  </div>

                  {/* Fecha de Pago */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Pago *
                    </label>
                    <input
                      type="date"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                      disabled={loading}
                      max={new Date().toISOString().split('T')[0]}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    />
                  </div>

                  {/* Comprobante */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comprobante de Pago *
                    </label>

                    {!proofPreview ? (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <Upload size={40} className="mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-600 mb-2">Arrastra una imagen o haz clic para seleccionar</p>
                        <p className="text-xs text-gray-500 mb-3">PNG, JPG o JPEG - Máximo 5MB</p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          disabled={loading}
                          className="hidden"
                          id="proof-upload"
                        />
                        <label
                          htmlFor="proof-upload"
                          className={`inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer ${loading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                          Seleccionar Archivo
                        </label>
                      </div>
                    ) : (
                      <div className="relative">
                        <img
                          src={proofPreview}
                          alt="Comprobante"
                          className="w-full h-64 object-contain bg-gray-50 rounded-lg border"
                        />
                        {!loading && (
                          <button
                            type="button"
                            onClick={() => {
                              setProofFile(null);
                              setProofPreview(null);
                            }}
                            className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Notas Adicionales */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notas Adicionales (Opcional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      disabled={loading}
                      rows={3}
                      placeholder="Agrega cualquier información adicional..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 resize-none"
                    />
                  </div>
                </>
              )}

              {paymentMethod === 'mercadopago' && (
                <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-xl flex flex-col items-center text-center">
                  <CreditCard size={40} className="text-blue-600 mb-3" />
                  <p className="text-blue-900 font-semibold text-lg">Pago Automático e Instantáneo</p>
                  <p className="text-blue-700 text-sm mt-2">
                    Al usar Mercado Pago, tu suscripción se renovará automáticamente al finalizar el pago. No necesitas subir comprobantes.
                  </p>
                </div>
              )}

              {/* Progress Bar */}
              {loading && uploadProgress > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Procesando solicitud...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>

                {paymentMethod === 'mercadopago' ? (
                  <button
                    type="button"
                    onClick={handleMercadoPagoCheckout}
                    disabled={loading || !selectedPlan}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg shadow-blue-200"
                  >
                    {loading ? (
                      <>
                        <Loader className="animate-spin mr-2" size={16} />
                        Procesando...
                      </>
                    ) : (
                      'Pagar con Mercado Pago'
                    )}
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading || !selectedPlan || !proofFile}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <Loader className="animate-spin mr-2" size={16} />
                        Enviando...
                      </>
                    ) : (
                      'Enviar Solicitud'
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RenewalRequestModal;